import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Building2Icon } from 'lucide-react';
import {
  Button,
  CardHeader,
  EmptyState,
  ErrorNotice,
  LoadingState,
  Panel,
  Pill,
  SelectInput,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  readEventPortalFeatures,
  saveEventPortalFeatures,
  type PortalFeatureOverrideWrites,
} from '@/lib/portal-features';

/**
 * The Client portal tab: what this event's client portal shows.
 *
 * An event on an enterprise portal shows everything, so the tab only says
 * whose portal it is on. Every other event is on the standard client portal
 * and follows the Settings default feature by feature, unless it is switched
 * on or off here for this event alone.
 */

/** What one feature's selector offers: follow the default, or force it either way. */
const CHOICE = { default: 'DEFAULT', shown: 'SHOWN', hidden: 'HIDDEN' } as const;
type Choice = (typeof CHOICE)[keyof typeof CHOICE];

function choiceOf(override: boolean | undefined): Choice {
  if (override === undefined) return CHOICE.default;
  return override ? CHOICE.shown : CHOICE.hidden;
}

function overrideOf(choice: Choice): boolean | null {
  if (choice === CHOICE.default) return null;
  return choice === CHOICE.shown;
}

export function ClientPortalTab({ eventId }: { readonly eventId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const features = useQuery({
    queryKey: ['event-portal-features', eventId],
    queryFn: ({ signal }) => readEventPortalFeatures(eventId, signal),
  });
  const [draft, setDraft] = useState<Record<string, Choice> | null>(null);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: (writes: PortalFeatureOverrideWrites) => saveEventPortalFeatures(eventId, writes),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['event-portal-features', eventId] });
      setDraft(null);
      setSaved(true);
    },
  });

  if (features.isPending) return <LoadingState label="Loading the client portal…" />;
  if (features.isError) {
    return <ErrorNotice title="Client portal unavailable" message={errorMessage(features.error)} />;
  }
  const { catalogue, defaults, overrides, enterprise } = features.data;

  if (enterprise) {
    return (
      <div className="mt-6">
        <Panel>
          <EmptyState
            icon={<Building2Icon aria-hidden className="size-6" />}
            title={`On ${enterprise.name}'s enterprise portal`}
            message="Every feature is on for an event on an enterprise portal. Their people, teams and settings are managed on the organization."
            action={
              <Button to={`/organizations/${enterprise.organizationId}`} variant="secondary">
                Open {enterprise.name}
              </Button>
            }
          />
        </Panel>
      </div>
    );
  }

  const choices: Record<string, Choice> =
    draft ?? Object.fromEntries(catalogue.map((feature) => [feature.key, choiceOf(overrides[feature.key])]));
  const dirty = draft !== null && catalogue.some((feature) => choices[feature.key] !== choiceOf(overrides[feature.key]));

  function set(key: string, choice: Choice): void {
    setSaved(false);
    setDraft({ ...choices, [key]: choice });
  }

  return (
    <div className="mt-6 space-y-5">
      <Panel className="overflow-hidden">
        <div className="p-5 pb-4">
          <CardHeader
            className="mb-0"
            title="Standard client portal"
            description="What this event's client portal shows. Each feature follows the default from Settings unless it is switched on or off here for this event."
            aside={
              <Link to="/settings?tab=portal" className="font-bold text-brand hover:underline">
                Defaults in Settings
              </Link>
            }
          />
        </div>
        <ul className="divide-y divide-portal-line border-t border-portal-line">
          {catalogue.map((feature) => {
            const defaultOn = defaults[feature.key] ?? feature.defaultOn;
            const id = `portal-feature-${feature.key}`;
            return (
              <li key={feature.key} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <label htmlFor={id} className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-portal-small font-bold">{feature.label}</span>
                    {feature.available ? null : <Pill tone="neutral">Not on the standard portal yet</Pill>}
                  </span>
                  <span className="block text-portal-micro text-portal-muted">{feature.description}</span>
                </label>
                <SelectInput
                  id={id}
                  density="compact"
                  value={choices[feature.key] ?? CHOICE.default}
                  disabled={save.isPending}
                  onChange={(event) => set(feature.key, event.target.value as Choice)}
                  className="w-56 shrink-0"
                >
                  <option value={CHOICE.default}>{`Default (${defaultOn ? 'shown' : 'hidden'})`}</option>
                  <option value={CHOICE.shown}>Shown for this event</option>
                  <option value={CHOICE.hidden}>Hidden for this event</option>
                </SelectInput>
              </li>
            );
          })}
        </ul>
      </Panel>

      {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}

      <div className="flex items-center gap-3">
        <Button
          disabled={save.isPending || !dirty}
          onClick={() =>
            save.mutate(Object.fromEntries(catalogue.map((feature) => [feature.key, overrideOf(choices[feature.key] ?? CHOICE.default)])))
          }
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="secondary" disabled={save.isPending || !dirty} onClick={() => setDraft(null)}>
          Reset
        </Button>
        {saved && !dirty ? <span className="text-portal-micro font-bold text-emerald-600">Saved</span> : null}
      </div>
    </div>
  );
}
